<script lang="ts">
  import { tick } from 'svelte'
  import { resolve } from '$app/paths'
  import SEO from '#lib/components/SEO.svelte'
  import CTA from '#lib/components/CTA.svelte'
  import type { PageProps } from './$types'

  let { data, form }: PageProps = $props()

  // Which tile (index into data.ids) is playing; null = all tiles are posters
  let playing: number | null = $state(null)
  const tileEls: (HTMLElement | undefined)[] = $state([])

  async function play(index: number) {
    playing = index
    await tick()
    tileEls[index]?.focus()
  }
</script>

<SEO
  title="Taghriba — Ally episodes"
  description="Episodes 5 to 31 of Taghriba, for PAPA Allies."
/>

<article class="bg-brand-sand-light min-h-screen">
  <div class="from-brand-green to-brand-green-deep bg-linear-to-br text-white">
    <div
      class="container mx-auto px-5 py-16 text-center [text-shadow:0_1px_3px_rgb(0_0_0/0.35)] lg:py-24"
    >
      <p class="font-display mb-4 text-sm tracking-widest uppercase opacity-70">For PAPA Allies</p>
      <h1 class="font-display mb-6 text-5xl font-bold lg:text-8xl">Taghriba</h1>
      {#if data.unlocked}
        <p class="mx-auto max-w-2xl text-lg opacity-80">
          Episodes {data.firstEpisode} to {data.firstEpisode + data.ids.length - 1}. Thank you for
          standing with the series.
        </p>
      {:else}
        <p class="mx-auto max-w-2xl text-lg opacity-80">
          Episodes 5 to 31 are for PAPA Allies. Enter the access code from your welcome email.
        </p>
      {/if}
    </div>
  </div>

  {#if data.unlocked}
    <section class="container mx-auto px-5 py-12 lg:py-16">
      <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
        {#each data.ids as id, i (id)}
          {@const n = data.firstEpisode + i}
          <article
            bind:this={tileEls[i]}
            tabindex="-1"
            class="group relative aspect-video overflow-hidden outline-none"
          >
            {#if playing === i}
              <iframe
                src={`https://player.vimeo.com/video/${id}?autoplay=1&dnt=1&title=0&byline=0&portrait=0`}
                title={`Taghriba Episode ${String(n)}`}
                allow="autoplay; fullscreen; picture-in-picture"
                class="absolute inset-0 h-full w-full border-0 bg-black"
              ></iframe>
            {:else}
              <button
                type="button"
                onclick={() => play(i)}
                aria-label={`Play Episode ${String(n)}`}
                data-umami-event="taghriba-ally-play"
                data-umami-event-episode={n}
                class="absolute inset-0 h-full w-full cursor-pointer bg-neutral-900 text-white transition-colors hover:bg-neutral-800"
              >
                <span class="font-display absolute top-4 left-4 text-2xl font-bold lg:text-3xl">
                  Episode {n}
                </span>
                <span
                  class="bg-brand-sand absolute right-4 bottom-4 flex h-12 w-12 items-center justify-center rounded-full shadow-2xl transition-transform group-hover:scale-110"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    class="ml-0.5 h-6 w-6 text-neutral-900"
                    aria-hidden="true"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </span>
              </button>
            {/if}
          </article>
        {/each}
      </div>
    </section>
  {:else}
    <section class="container mx-auto max-w-xl px-5 py-16 text-center lg:py-20">
      <form method="POST" action="?/unlock" class="flex flex-col gap-4">
        <label for="code" class="font-display text-xl font-bold text-black">Ally access code</label>
        <input
          id="code"
          name="code"
          type="text"
          required
          autocomplete="off"
          autocapitalize="none"
          spellcheck="false"
          aria-invalid={form?.error === true ? 'true' : undefined}
          aria-describedby={form?.error === true ? 'code-error' : undefined}
          class="border border-black/30 bg-white px-4 py-3 text-center text-lg text-black"
        />
        {#if form?.error === true}
          <p id="code-error" role="alert" class="text-red-700">
            That code didn't work. Check your welcome email and try again.
          </p>
        {/if}
        <button
          type="submit"
          class="font-display bg-black px-5 py-4 text-xl font-bold text-white transition-all duration-300 hover:scale-105"
        >
          Unlock episodes
        </button>
      </form>

      <p class="mt-10 text-black/80">Not an Ally yet? Episodes 1 to 4 are free for everyone.</p>
      <div class="mt-6">
        <CTA
          href={resolve('/donations')}
          text="Become an Ally"
          data-umami-event="taghriba-ally-join-cta"
        />
      </div>
    </section>
  {/if}
</article>
